/**
 * Agregaciones puras sobre servicios_uso_dia / servicios_uso_hora.
 *
 * Regla de no duplicar: Google se estima por métricas (filas «estimado») y, días
 * después, llega la factura real de BigQuery (filas «facturado», `factura_*`).
 * Para un día que ya tiene factura, la estimación de Google se deja de sumar
 * al costo —seguiría siendo la misma plata contada dos veces— pero su cantidad
 * (lecturas, GB…) sí se conserva.
 */
import { CATEGORIAS, type ServicioUsoDoc } from './costosUtil';

export interface ServicioFila extends ServicioUsoDoc {
  /** Estimación de Google reemplazada por la factura real de ese día. */
  superada: boolean;
  /** Fila «AWS · total del día» cuando ya están las filas por servicio: se usa solo para la serie diaria. */
  esTotal: boolean;
}

/** Costo que cuenta para los totales: null si no hay precio o si lo reemplazó la factura. */
export function costoVigente(f: ServicioFila): number | null {
  return f.superada || f.esTotal || f.costo == null || !Number.isFinite(f.costo) ? null : f.costo;
}

export function prepararDias(docs: ServicioUsoDoc[]): ServicioFila[] {
  const conFactura = new Set<string>();
  const awsConDetalle = new Set<string>();
  for (const d of docs) {
    if (d.proveedor === 'google_cloud' && d.fuenteCosto === 'facturado') conFactura.add(d.fecha);
    if (d.proveedor === 'aws' && d.servicio !== 'total') awsConDetalle.add(d.fecha);
  }
  return docs.map((d) => ({
    ...d,
    cantidad: Number(d.cantidad) || 0,
    superada: d.proveedor === 'google_cloud' && d.fuenteCosto !== 'facturado'
      && d.costo != null && conFactura.has(d.fecha),
    esTotal: d.proveedor === 'aws' && d.servicio === 'total' && awsConDetalle.has(d.fecha),
  }));
}

/** Costo diario de AWS (registro biométrico): el total del día si existe; si no, la suma de sus servicios. */
export function awsPorDia(filas: ServicioFila[]): Array<{ fecha: string; costo: number }> {
  const totales = new Map<string, number>();
  const sumas = new Map<string, number>();
  for (const f of filas) {
    if (f.proveedor !== 'aws' || f.costo == null) continue;
    if (f.servicio === 'total') totales.set(f.fecha, f.costo);
    else sumas.set(f.fecha, (sumas.get(f.fecha) ?? 0) + f.costo);
  }
  const fechas = new Set([...totales.keys(), ...sumas.keys()]);
  return [...fechas].sort().map((fecha) => ({ fecha, costo: totales.get(fecha) ?? sumas.get(fecha) ?? 0 }));
}

export interface FilaServicio {
  clave: string;
  categoria: string;
  proveedor: string;
  nombre: string;
  unidad: string;
  cantidad: number;
  /** null si ningún documento del rango tiene costo. */
  costo: number | null;
  marca: string;
  dias: number;
  diasSuperados: number;
}

const PRIORIDAD_MARCA = ['facturado', 'estimado', 'fijo'];

export function agruparPorServicio(filas: ServicioFila[]): FilaServicio[] {
  const mapa = new Map<string, FilaServicio>();
  for (const f of filas) {
    if (f.esTotal) continue;
    const clave = `${f.categoria}/${f.proveedor}/${f.servicio}`;
    const a = mapa.get(clave) ?? {
      clave, categoria: f.categoria, proveedor: f.proveedor, nombre: f.nombre || f.servicio,
      unidad: f.unidad, cantidad: 0, costo: null, marca: f.fuenteCosto ?? 'estimado', dias: 0, diasSuperados: 0,
    };
    a.cantidad += f.cantidad;
    const c = costoVigente(f);
    if (c != null) a.costo = (a.costo ?? 0) + c;
    a.dias += 1;
    if (f.superada) a.diasSuperados += 1;
    const pa = PRIORIDAD_MARCA.indexOf(a.marca);
    const pf = PRIORIDAD_MARCA.indexOf(f.fuenteCosto ?? 'estimado');
    if (pf !== -1 && (pa === -1 || pf < pa)) a.marca = f.fuenteCosto as string;
    mapa.set(clave, a);
  }
  return [...mapa.values()].sort((x, y) =>
    (y.costo ?? -1) - (x.costo ?? -1) || x.nombre.localeCompare(y.nombre));
}

export interface ResumenCostos {
  total: number;
  porCategoria: Record<string, number>;
  /** fecha → costo por categoría. */
  porDia: Map<string, Record<string, number>>;
  diasSuperados: number;
}

export function resumirCostos(filas: ServicioFila[]): ResumenCostos {
  const porCategoria: Record<string, number> = Object.fromEntries(CATEGORIAS.map((c) => [c, 0]));
  const porDia = new Map<string, Record<string, number>>();
  const fechasSuperadas = new Set<string>();
  let total = 0;
  for (const f of filas) {
    if (f.superada) fechasSuperadas.add(f.fecha);
    const c = costoVigente(f);
    if (c == null) continue;
    total += c;
    porCategoria[f.categoria] = (porCategoria[f.categoria] ?? 0) + c;
    const dia = porDia.get(f.fecha) ?? {};
    dia[f.categoria] = (dia[f.categoria] ?? 0) + c;
    porDia.set(f.fecha, dia);
  }
  return { total, porCategoria, porDia, diasSuperados: fechasSuperadas.size };
}

/**
 * Minutos de participante por rol (médico / paciente / agente) en el rango, más
 * los de salas que no son una consulta de este entorno (`sin_consulta`: triage
 * por voz, asistente de voz o el otro entorno, que comparte LiveKit). Se facturan
 * igual, pero van aparte para no mezclarlos con los de las consultas.
 */
export interface MinutosPorRol { medico: number; paciente: number; agente: number; sinConsulta: number; total: number }
export function minutosPorRol(filas: ServicioFila[]): MinutosPorRol {
  const r: MinutosPorRol = { medico: 0, paciente: 0, agente: 0, sinConsulta: 0, total: 0 };
  for (const f of filas) {
    if (f.proveedor !== 'livekit' || f.servicio !== 'participante_minutos') continue;
    r.total += f.cantidad;
    const d = f.desglose ?? {};
    r.medico += Number(d.medico) || 0;
    r.paciente += Number(d.paciente) || 0;
    r.agente += Number(d.agente) || 0;
    r.sinConsulta += Number(d.sin_consulta) || 0;
  }
  return r;
}
